import { useEffect, useRef } from 'react';
import GUI, { Controller } from 'lil-gui';
import useSimulationStore from '../store/simulationStore';

// Helper to safely add/update controllers
const addOrUpdateController = (
  folder: GUI,
  ref: React.MutableRefObject<Controller | null>,
  stateObj: React.MutableRefObject<any>,
  propName: string,
  min?: number,
  max?: number,
  step?: number
) => {
  // console.log(`addOrUpdateController called for: ${propName}`, { folderExists: !!folder, refExists: !!ref.current, stateValue: stateObj.current[propName] });
  if (!ref.current) {
    // console.log(` -> Creating new controller for ${propName}`);
    ref.current = folder.add(stateObj.current, propName, min, max, step).name(propName.charAt(0).toUpperCase() + propName.slice(1).replace(/([A-Z])/g, ' $1'));
    // console.log(`    -> New controller instance:`, ref.current);
  } else {
    // console.log(` -> Updating display for existing controller ${propName}`);
    ref.current.updateDisplay();
  }
  return ref.current;
};

export const useStyleSettingsGui = (gui: GUI | null) => {
  // Select state and actions from the store
  const storeState = useSimulationStore((state) => ({ 
    // Material
    metalness: state.metalness,
    roughness: state.roughness,
    emissiveIntensity: state.emissiveIntensity,
    // Bloom
    bloomIntensity: state.bloomIntensity,
    bloomThreshold: state.bloomThreshold,
    bloomRadius: state.bloomRadius,
    // Box
    boxStyle: state.boxStyle,
    // Setters
    setMetalness: state.setMetalness,
    setRoughness: state.setRoughness,
    setEmissiveIntensity: state.setEmissiveIntensity,
    setBloomIntensity: state.setBloomIntensity,
    setBloomThreshold: state.setBloomThreshold,
    setBloomRadius: state.setBloomRadius,
    setBoxStyle: state.setBoxStyle,
    // UI Features
    uiFeatures: state.uiFeatures,
  })); 

  // Use refs to hold controller instances and a stable local state object for lil-gui
  const localGuiState = useRef({ ...storeState }); // Initialize with all values
  // Store refs to controllers, initialize lazily
  const controllersRef = useRef<Record<string, React.MutableRefObject<Controller | null>>>({}); 
  const foldersRef = useRef<Record<string, GUI | null>>({});

  // Effect 1: Update local ref state & GUI display only when Zustand state changes
  useEffect(() => {
    let changed = false;
    // Explicitly type key and ensure it's a valid key for the objects
    for (const key in storeState) {
        const typedKey = key as keyof typeof storeState & keyof typeof localGuiState.current;
        if (typeof storeState[typedKey] !== 'function' && 
            localGuiState.current[typedKey] !== storeState[typedKey]) {
            (localGuiState.current[typedKey] as any) = storeState[typedKey]; // Use type assertion for assignment
            changed = true;
        }
    }
    if (changed && gui) {
        // Update all controllers linked in controllersRef
        Object.values(controllersRef.current).forEach(controller => controller?.current?.updateDisplay());
    }
  }, [storeState, gui]); // Depend on the whole state slice

  // Effect 2: Setup GUI folders and controllers ONCE
  useEffect(() => {
    if (!gui || storeState.uiFeatures.useNewControls) return;

    const mainFolderTitle = 'Style Settings';
    console.log("Attempting to setup Style Settings GUI for:", gui);
    
    // Get or create the main folder
    let mainFolder = foldersRef.current[mainFolderTitle];
    if (!mainFolder) {
        mainFolder = gui.addFolder(mainFolderTitle); 
        foldersRef.current[mainFolderTitle] = mainFolder;
    }
    console.log("Main Style Settings folder:", mainFolder);

    // --- Sub Folders --- 
    const createFolder = (title: string) => {
        if (!mainFolder) {
            console.error("Main folder is not available!");
            return null;
        }
        // Get or create the sub-folder
        let folder = foldersRef.current[title];
        if (!folder) {
            folder = mainFolder.addFolder(title);
            foldersRef.current[title] = folder;
        }
        console.log(`Sub-folder ${title}:`, folder);
        return folder;
    }

    const materialFolder = createFolder('Material');
    const bloomFolder = createFolder('Bloom');
    // Removed: disabled post-processing effects
    // const dofFolder = createFolder('Depth of Field');
    // const tmFolder = createFolder('Tone Mapping');
    // const vigFolder = createFolder('Vignette');
    // const noiseFolder = createFolder('Noise');

    if (materialFolder) {
        // console.log("Setting up Material controllers. Current local state:", { ...localGuiState.current });
        // Ensure ref object exists for the property
        if (!controllersRef.current.metalness) controllersRef.current.metalness = { current: null };
        addOrUpdateController(materialFolder, controllersRef.current.metalness, localGuiState, 'metalness', 0, 1)
            ?.onChange(storeState.setMetalness); // Add null check for safety
        
        if (!controllersRef.current.roughness) controllersRef.current.roughness = { current: null };
        addOrUpdateController(materialFolder, controllersRef.current.roughness, localGuiState, 'roughness', 0, 1)
            ?.onChange(storeState.setRoughness);
        
        if (!controllersRef.current.emissiveIntensity) controllersRef.current.emissiveIntensity = { current: null };
        addOrUpdateController(materialFolder, controllersRef.current.emissiveIntensity, localGuiState, 'emissiveIntensity', 0, 2, 0.1)
            ?.onChange(storeState.setEmissiveIntensity);
    }
    
    // Bloom controls
    if (bloomFolder) {
        if (!controllersRef.current.bloomIntensity) controllersRef.current.bloomIntensity = { current: null };
        addOrUpdateController(bloomFolder, controllersRef.current.bloomIntensity, localGuiState, 'bloomIntensity', 0, 3, 0.1)
            ?.onChange(storeState.setBloomIntensity);
        
        if (!controllersRef.current.bloomThreshold) controllersRef.current.bloomThreshold = { current: null };
        addOrUpdateController(bloomFolder, controllersRef.current.bloomThreshold, localGuiState, 'bloomThreshold', 0, 1, 0.05)
            ?.onChange(storeState.setBloomThreshold);
        
        if (!controllersRef.current.bloomRadius) controllersRef.current.bloomRadius = { current: null };
        addOrUpdateController(bloomFolder, controllersRef.current.bloomRadius, localGuiState, 'bloomRadius', 0, 1, 0.05)
            ?.onChange(storeState.setBloomRadius);
    }
    
    // Box Style folder
    if (!foldersRef.current.boxFolder) {
        foldersRef.current.boxFolder = gui.addFolder('Box Style');
        controllersRef.current.boxStyle = { current: null };
    }
    const boxFolder = foldersRef.current.boxFolder;
    if (boxFolder) {
        if (!controllersRef.current.boxStyle.current) {
            controllersRef.current.boxStyle.current = boxFolder.add(localGuiState.current, 'boxStyle', ['normal', 'hidden', 'glass', 'forcefield'])
                .name('Style')
                .onChange(storeState.setBoxStyle);
        } else {
            controllersRef.current.boxStyle.current.updateDisplay();
        }
    }
    
    // Old post-processing effect controls removed (effects disabled due to memory leaks)
    
    // Cleanup function: remove folders when effect re-runs or component unmounts
    return () => {
      console.log("Cleaning up Style Settings GUI folders...");
      Object.values(foldersRef.current).forEach(folder => {
        if (folder) {
          try {
            // Check if folder is already destroyed before attempting to destroy again
            // lil-gui might not have a standard isDestroyed flag, rely on internal state if needed or just try-catch
            folder.destroy(); 
          } catch (e) {
            console.warn("Error destroying folder during cleanup:", e);
          }
        }
      });
      // Reset refs
      foldersRef.current = {};
      controllersRef.current = {}; 
      console.log("Cleanup complete.");
    };

  // Update dependency array to only include gui
  }, [gui]); 
}; 